import type { StaffCatalogPlaceReviewPage, StaffCatalogPlaceReviewQuery } from "@lafayette/schemas";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useCatalogRuntime } from "../runtime";
import { REVIEW_PAGE_SIZE } from "./review-filters";

// Reads of the places routes, cached by TanStack Query. The place view is keyed by the item's version: a
// save elsewhere on the page moves the version and the view is read again (the geography status can
// change with a plain edit). The previous view stays on screen while the new one loads, so an open
// search panel is not torn down by a refetch.

export const placeKeys = {
  all: (itemId: string) => ["catalog", "place", itemId] as const,
  view: (itemId: string, version: number) => ["catalog", "place", itemId, version] as const,
  review: (query: StaffCatalogPlaceReviewQuery) => ["catalog", "place-review", query] as const,
};

export function usePlaceView(itemId: string, version: number, enabled = true) {
  const { api } = useCatalogRuntime();
  return useQuery({
    queryKey: placeKeys.view(itemId, version),
    queryFn: ({ signal }) => api.getPlace(itemId, { signal }),
    enabled,
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** The review queue, one keyset page at a time. Each page passes the previous page's cursor back. */
export function usePlaceReview(query: StaffCatalogPlaceReviewQuery, enabled = true) {
  const { api } = useCatalogRuntime();
  return useInfiniteQuery({
    queryKey: placeKeys.review(query),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }): Promise<StaffCatalogPlaceReviewPage> =>
      api.listPlaceReview(
        {
          ...query,
          limit: REVIEW_PAGE_SIZE,
          ...(pageParam === null ? {} : { cursor: pageParam }),
        },
        { signal },
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
